import React, { useState } from "react";
import {
  Settings,
  User,
  Activity,
  Target,
  Award,
  Info,
  Brain,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import SEO, { PAGE_SEO } from "./SEO";
import { UserProfile, EquipmentRecommendation } from "../types";
import { aiService } from "../services/aiService";
import { getPersonaFromScore, estimateSwingSpeed, estimateHandicapFromScore, getAIEstimateExplanation } from "../utils/golfPersona";
import { HeightPicker, BirthdatePicker } from "./ui/WheelPicker";

interface EquipmentFittingProps {
  userProfile: UserProfile | null;
  onProfileUpdate?: (profile: UserProfile) => Promise<boolean> | void;
}

// Helper to convert price ranges to tier labels
// REAL MARKET PRICING: Drivers $100-200-300+, Irons $400-700-1200+, Wedges $50-75-105+
function getPriceTier(priceRange: string, category?: string): { tier: string; colorClass: string } {
  // Parse the first price from strings like "$400-500", "$1,400-1,600"
  const match = priceRange.match(/\$?([\d,]+)/);
  const price = match ? parseInt(match[1].replace(',', '')) : 500;
  
  // Category-specific thresholds matching real market pricing
  if (category === 'irons') {
    // Iron sets: Value $400-700, Mid $700-1200, Premium $1200+
    if (price <= 700) return { tier: 'Value', colorClass: 'bg-emerald-100 text-emerald-700' };
    if (price <= 1200) return { tier: 'Mid-Range', colorClass: 'bg-blue-100 text-blue-700' };
    return { tier: 'Premium', colorClass: 'bg-purple-100 text-purple-700' };
  } else if (category === 'driver') {
    // Drivers: Value $100-200, Mid $200-300, Premium $300+
    if (price <= 200) return { tier: 'Value', colorClass: 'bg-emerald-100 text-emerald-700' };
    if (price <= 300) return { tier: 'Mid-Range', colorClass: 'bg-blue-100 text-blue-700' };
    return { tier: 'Premium', colorClass: 'bg-purple-100 text-purple-700' };
  } else if (category === 'wedge' || category === 'wedges') {
    // Wedges: Value $50-75, Mid $75-105, Premium $105+
    if (price <= 75) return { tier: 'Value', colorClass: 'bg-emerald-100 text-emerald-700' };
    if (price <= 105) return { tier: 'Mid-Range', colorClass: 'bg-blue-100 text-blue-700' };
    return { tier: 'Premium', colorClass: 'bg-purple-100 text-purple-700' };
  } else {
    // Fairway woods/hybrids: same as drivers
    // Value $100-200, Mid $200-300, Premium $300+
    if (price <= 200) return { tier: 'Value', colorClass: 'bg-emerald-100 text-emerald-700' };
    if (price <= 300) return { tier: 'Mid-Range', colorClass: 'bg-blue-100 text-blue-700' };
    return { tier: 'Premium', colorClass: 'bg-purple-100 text-purple-700' };
  }
}

const EquipmentFitting: React.FC<EquipmentFittingProps> = ({ userProfile, onProfileUpdate }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [fittingData, setFittingData] = useState({
    averageScore: undefined as number | undefined,
    heightFeet: 5,
    heightInches: 8,
    weight: 180,
    age: 35,
    birthdate: undefined as string | undefined,
    birthMonth: undefined as number | undefined,
    birthDay: undefined as number | undefined,
    birthYear: undefined as number | undefined,
    handicap: "" as number | "",
    swingSpeed: undefined as number | undefined,
    swingTempo: "medium" as "slow" | "medium" | "fast",
    ballFlight: "straight" as "fade" | "draw" | "straight" | "slice" | "hook",
    currentIssues: [] as string[],
    budget: "medium",
    priority: "distance",
    // Advanced fields
    handSize: undefined as string | undefined,
    gripType: undefined as string | undefined,
    gripPressure: undefined as string | undefined,
    attackAngle: undefined as string | undefined,
  });

  // Calculate age from birthdate helper
  const calculateAgeFromBirthdate = (birthdate: string): number => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Calculate age from month/day/year
  const calculateAgeFromParts = (year: number, month: number, day: number): number => {
    const today = new Date();
    let age = today.getFullYear() - year;
    const birthdayThisYear = new Date(today.getFullYear(), month - 1, day);
    if (today < birthdayThisYear) {
      age--;
    }
    return age;
  };

  // Update fitting data when userProfile loads or changes
  React.useEffect(() => {
    if (userProfile) {
      // If profile has birthdate, parse it into month/day/year and calculate age
      let birthMonth: number | undefined;
      let birthDay: number | undefined;
      let birthYear: number | undefined;
      let profileAge: number;
      
      if (userProfile.birthdate) {
        const birthDate = new Date(userProfile.birthdate);
        birthMonth = birthDate.getMonth() + 1; // getMonth() returns 0-11
        birthDay = birthDate.getDate();
        birthYear = birthDate.getFullYear();
        profileAge = calculateAgeFromBirthdate(userProfile.birthdate);
      } else {
        profileAge = userProfile.age || 35;
      }
      
      setFittingData(prev => ({
        ...prev,
        averageScore: userProfile.averageScore || prev.averageScore,
        heightFeet: userProfile.height ? Math.floor(userProfile.height / 12) : prev.heightFeet,
        heightInches: userProfile.height ? userProfile.height % 12 : prev.heightInches,
        weight: userProfile.weight || prev.weight,
        age: profileAge || prev.age,
        birthdate: userProfile.birthdate || prev.birthdate,
        birthMonth: birthMonth || prev.birthMonth,
        birthDay: birthDay || prev.birthDay,
        birthYear: birthYear || prev.birthYear,
        handicap: userProfile.handicap || prev.handicap,
        swingSpeed: userProfile.swingSpeed || prev.swingSpeed,
        swingTempo: userProfile.swingTempo || prev.swingTempo,
        ballFlight: userProfile.ballFlight || prev.ballFlight,
        currentIssues: userProfile.currentIssues || prev.currentIssues,
        handSize: userProfile.handSize || prev.handSize,
        gripType: userProfile.gripType || prev.gripType,
        gripPressure: userProfile.gripPressure || prev.gripPressure,
        attackAngle: userProfile.attackAngle || prev.attackAngle,
      }));
    }
  }, [userProfile]);
  const [recommendations, setRecommendations] = useState<
    EquipmentRecommendation[]
  >([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Get user's persona for adaptive questions
  const userPersona = fittingData.averageScore 
    ? getPersonaFromScore(fittingData.averageScore)
    : null;

  const steps = [
    { title: "Your Golf Game", shortLabel: "Your game", icon: Award },
    { title: "Physical Stats", shortLabel: "Physical", icon: User },
    { title: "Swing Data", shortLabel: "Swing", icon: Activity },
    { title: "Performance Goals", shortLabel: "Goals", icon: Target },
    { title: "Recommendations", shortLabel: "Recs", icon: Award },
  ];

  const handleInputChange = (field: string, value: any) => {
    setFittingData((prev) => {
      const updated = { ...prev, [field]: value };
      
      // Reactive derived data: auto-recalculate dependent fields
      if (field === 'averageScore' && typeof value === 'number' && value > 0) {
        // Average score → recalculate swing speed estimate and handicap
        updated.swingSpeed = estimateSwingSpeed(value, prev.age || 35);
        updated.handicap = estimateHandicapFromScore(value);
      }
      
      if (field === 'age' && typeof value === 'number' && value > 0 && prev.averageScore) {
        updated.swingSpeed = estimateSwingSpeed(prev.averageScore, value);
      }
      
      return updated;
    });
  };

  const handleIssueToggle = (issue: string) => {
    setFittingData((prev) => ({
      ...prev,
      currentIssues: prev.currentIssues.includes(issue)
        ? prev.currentIssues.filter((i) => i !== issue)
        : [...prev.currentIssues, issue],
    }));
  };

  // Save physical stats to profile
  const savePhysicalStats = () => {
    if (!userProfile || !onProfileUpdate) return;

    // Build birthdate string from parts if all are provided
    const birthdate = fittingData.birthYear && fittingData.birthMonth && fittingData.birthDay
      ? `${fittingData.birthYear}-${String(fittingData.birthMonth).padStart(2, '0')}-${String(fittingData.birthDay).padStart(2, '0')}`
      : fittingData.birthdate;

    // Calculate age from birthdate if available
    const calculatedAge = (fittingData.birthYear && fittingData.birthMonth && fittingData.birthDay)
      ? calculateAgeFromParts(fittingData.birthYear, fittingData.birthMonth, fittingData.birthDay)
      : fittingData.age;

    const updatedProfile: UserProfile = {
      ...userProfile,
      age: calculatedAge,
      birthdate: birthdate,
      height: fittingData.heightFeet * 12 + fittingData.heightInches,
      weight: fittingData.weight,
      swingSpeed: fittingData.swingSpeed,
      swingTempo: fittingData.swingTempo,
      ballFlight: fittingData.ballFlight,
      equipmentTier: fittingData.budget as "budget" | "medium" | "premium", // Save tier preference
      updatedAt: new Date()
    };

    onProfileUpdate(updatedProfile);
    console.log('✅ Physical stats and equipment tier saved to profile');
  };

  const generateRecommendations = async () => {
    setIsAnalyzing(true);

    try {
      let recs: EquipmentRecommendation[] = [];

      // ALWAYS use AI for intelligent recommendations
      if (userProfile) {
        // Build birthdate string from parts if all are provided
        const birthdate = fittingData.birthYear && fittingData.birthMonth && fittingData.birthDay
          ? `${fittingData.birthYear}-${String(fittingData.birthMonth).padStart(2, '0')}-${String(fittingData.birthDay).padStart(2, '0')}`
          : fittingData.birthdate || userProfile.birthdate;

        // Calculate age from birthdate if available - ensure we always have a valid number
        const calculatedAge: number = (fittingData.birthYear && fittingData.birthMonth && fittingData.birthDay)
          ? calculateAgeFromParts(fittingData.birthYear, fittingData.birthMonth, fittingData.birthDay)
          : (fittingData.age || userProfile.age || 35); // Default to 35 if all else fails

        // AUTO-ESTIMATE swing speed from persona if not provided
        // This is critical - if user didn't manually enter or click "Let AI Estimate", we use their averageScore
        let effectiveSwingSpeed = fittingData.swingSpeed;
        if (!effectiveSwingSpeed && fittingData.averageScore) {
          // averageScore exists, so estimate from it (userPersona is also available in this case)
          effectiveSwingSpeed = estimateSwingSpeed(fittingData.averageScore, calculatedAge);
          console.log(`🎯 Auto-estimated swing speed: ${effectiveSwingSpeed} mph from score ${fittingData.averageScore}`);
        } else if (!effectiveSwingSpeed) {
          // No averageScore - use handicap-based fallback
          const hcp = typeof fittingData.handicap === 'number' ? fittingData.handicap : 18;
          effectiveSwingSpeed = hcp < 10 ? 100 : hcp < 20 ? 92 : 85;
          console.log(`🎯 Default swing speed based on handicap ${hcp}: ${effectiveSwingSpeed} mph`);
        }

        const profile: UserProfile = {
          ...userProfile!,
          handicap: typeof fittingData.handicap === 'number' ? fittingData.handicap : (userPersona?.estimatedHandicap || 18),
          height: fittingData.heightFeet * 12 + fittingData.heightInches,
          age: calculatedAge,
          birthdate: birthdate,
          weight: fittingData.weight,
          swingSpeed: effectiveSwingSpeed,
          swingTempo: fittingData.swingTempo,
          ballFlight: fittingData.ballFlight,
          skillLevel: (userPersona?.skillLevel as UserProfile['skillLevel']) || userProfile!.skillLevel,
          currentIssues: fittingData.currentIssues,
          clubDistances: userProfile!.clubDistances || [],
          golfBag: userProfile!.golfBag || [],
          preferredUnits: userProfile!.preferredUnits || "yards",
          createdAt: userProfile!.createdAt,
          updatedAt: new Date(),
          // Pass additional data for better fitting
          averageScore: fittingData.averageScore,
          handSize: fittingData.handSize as UserProfile['handSize'],
          attackAngle: fittingData.attackAngle as UserProfile['attackAngle'],
          // Pass equipment tier for budget-filtered recommendations
          equipmentTier: fittingData.budget as "budget" | "medium" | "premium",
        };

        recs = await aiService.getAIEquipmentRecommendations({
          userProfile: profile,
          budget: fittingData.budget,
          priorities: [fittingData.priority],
          currentIssues: fittingData.currentIssues,
        });
      } else {
        // Fallback to rule-based recommendations
        recs = [];

        // Driver recommendations
        const swingSpeed = fittingData.swingSpeed || 95; // Default to average
        recs.push({
          category: "driver",
          recommendations: [
            {
              brand: "TaylorMade",
              model: "Stealth 2",
              specs: {
                loft:
                  swingSpeed < 90
                    ? "12°"
                    : swingSpeed > 105
                      ? "9°"
                      : "10.5°",
                shaft:
                  swingSpeed < 85
                    ? "Regular Graphite"
                    : swingSpeed < 95
                      ? "Stiff Graphite"
                      : "X-Stiff Steel",
                adjustability: "Yes",
              },
              reasoning: `Based on your ${swingSpeed} mph swing speed and ${fittingData.ballFlight} ball flight, this driver will optimize launch conditions.`,
              priceRange: "$400-500",
              alternatives: ["Callaway Paradym", "Ping G430"],
              detailedSpecs: {
                shaftDetails: {
                  model: swingSpeed < 85 ? "Fujikura Ventus Red TR" : swingSpeed < 95 ? "Project X HZRDUS Smoke RDX" : "Mitsubishi Tensei AV Blue",
                  weight: swingSpeed < 85 ? "55g" : swingSpeed < 95 ? "60g" : "65g",
                  torque: swingSpeed < 85 ? "4.8°" : swingSpeed < 95 ? "4.5°" : "4.2°",
                  launch: swingSpeed < 90 ? "high" : swingSpeed < 100 ? "mid" : "low",
                  spin: swingSpeed < 90 ? "mid" : swingSpeed < 100 ? "mid" : "low"
                },
                gripDetails: {
                  model: "Golf Pride Tour Velvet 360",
                  size: "standard",
                  material: "rubber",
                  wraps: "0 wraps"
                },
                technologyHighlights: ["60X Carbon Twist Face for maximum ball speed", "3D Carbon Crown for optimal weight distribution", "Speed Injected Twist Face for consistent performance"]
              }
            },
          ],
        });

        // Iron recommendations
        recs.push({
          category: "irons",
          recommendations: [
            {
              brand: !fittingData.handicap || fittingData.handicap > 15
                ? "Callaway"
                : fittingData.handicap > 8
                  ? "TaylorMade"
                  : "Mizuno",
              model: !fittingData.handicap || fittingData.handicap > 15
                ? "Paradym Ai Smoke"
                : fittingData.handicap > 8
                  ? "P790"
                  : "JPX 923 Hot Metal",
              specs: {
                type:
                  !fittingData.handicap || fittingData.handicap > 15
                    ? "Game Improvement"
                    : fittingData.handicap > 8
                      ? "Players Distance"
                      : "Players",
                shaft:
                  swingSpeed < 85
                    ? "Regular Graphite"
                    : swingSpeed < 95
                      ? "Stiff Graphite"
                      : "X-Stiff Steel",
                lie: "Standard",
                set: "5-PW",
              },
              reasoning: `${fittingData.handicap ? `Your ${fittingData.handicap} handicap` : "Your skill level"} suggests ${!fittingData.handicap || fittingData.handicap > 15 ? "maximum forgiveness" : fittingData.handicap > 8 ? "balanced performance" : "workability focused"} irons.`,
              priceRange: !fittingData.handicap || fittingData.handicap > 15 ? "$600-800" : "$800-1200",
              alternatives: [
                "Titleist T-Series",
                "Mizuno JPX",
                "Callaway Apex",
              ],
              detailedSpecs: {
                shaftDetails: {
                  model: swingSpeed < 85 ? "UST Recoil ESX" : swingSpeed < 95 ? "Nippon NS Pro Modus 3 Tour 105" : "Project X LS",
                  weight: swingSpeed < 85 ? "65g" : swingSpeed < 95 ? "105g" : "120g",
                  torque: swingSpeed < 85 ? "4.5°" : swingSpeed < 95 ? "2.8°" : "2.5°",
                  launch: !fittingData.handicap || fittingData.handicap > 15 ? "high" : fittingData.handicap > 8 ? "mid" : "low",
                  spin: !fittingData.handicap || fittingData.handicap > 15 ? "high" : fittingData.handicap > 8 ? "mid" : "low"
                },
                gripDetails: {
                  model: "Golf Pride Tour Velvet 360",
                  size: "standard",
                  material: "rubber",
                  wraps: "0 wraps"
                },
                technologyHighlights: [
                  !fittingData.handicap || fittingData.handicap > 15
                    ? "A.I. Designed Face for maximum forgiveness and distance"
                    : fittingData.handicap > 8
                      ? "SpeedFoam Air for enhanced feel and distance"
                      : "Chromoly 4140M Construction for exceptional feel and workability"
                ]
              }
            },
          ],
        });
      }

      setRecommendations(recs);
      setCurrentStep(4);
    } catch (error) {
      console.error(
        "AI equipment recommendations failed, falling back to rule-based:",
        error,
      );

      // Fallback to basic recommendations
      const basicRecs: EquipmentRecommendation[] = [
        {
          category: "driver",
          recommendations: [
            {
              specs: { loft: "10.5°", shaft: "Regular Graphite" },
              reasoning: "Standard driver recommendation for your profile.",
              priceRange: "$300-500",
              alternatives: ["Various brands available"],
            },
          ],
        },
      ];
      setRecommendations(basicRecs);
      setCurrentStep(4);
    }

    setIsAnalyzing(false);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
              🎯 What's Your Typical 18-Hole Score?
            </h2>
            <p className="text-gray-600 text-center mb-8">
              This single answer helps us estimate 10+ key metrics for perfect equipment recommendations!
            </p>

            {/* Score Slider */}
            <div className="max-w-2xl mx-auto px-1 sm:px-0">
              <div className="text-center mb-8">
                <div className="text-6xl font-bold text-blue-600 mb-2">
                  {fittingData.averageScore || 95}
                </div>
                <div className="text-gray-600 text-sm">Typical score</div>
              </div>

              <input
                type="range"
                min="65"
                max="150"
                value={fittingData.averageScore || 95}
                onChange={(e) => handleInputChange("averageScore", Number(e.target.value))}
                className="w-full h-3 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-lg appearance-none cursor-pointer touch-pan-x"
              />
              <div className="grid grid-cols-3 gap-1 sm:gap-2 mt-2 text-[10px] leading-tight sm:text-xs text-gray-500 px-0.5">
                <span className="text-left min-w-0 flex flex-col sm:block">
                  <span className="font-medium text-gray-700">65</span>
                  <span className="text-gray-500">Pro</span>
                </span>
                <span className="text-center min-w-0 flex flex-col items-center sm:block">
                  <span className="font-medium text-gray-700">95</span>
                  <span className="text-gray-500">Average</span>
                </span>
                <span className="text-right min-w-0 flex flex-col items-end sm:block">
                  <span className="font-medium text-gray-700">150</span>
                  <span className="text-gray-500">Beginner</span>
                </span>
              </div>

              {/* Persona Display */}
              {userPersona && (
                <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border-2 border-blue-200">
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl mb-3">{userPersona.icon}</div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{userPersona.name}</h4>
                    <p className="text-gray-600 text-sm mb-4">{userPersona.description}</p>
                    <div className="bg-white rounded-lg p-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Estimated Handicap:</span>
                        <span className="font-bold text-green-600">{userPersona.estimatedHandicap}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Estimated Swing Speed:</span>
                        <span className="font-bold text-blue-600">{userPersona.estimatedSwingSpeed} mph</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Typical Ball Flight:</span>
                        <span className="font-bold text-purple-600 capitalize">{userPersona.typicalBallFlight}</span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-4">
                      💡 We'll use these estimates to pre-fill the next steps. You can adjust any field!
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-8">
            <div style={{ textAlign: "center", marginBottom: "2rem" }}>
              <h2
                style={{
                  fontSize: "2rem",
                  fontWeight: "800",
                  color: "var(--gray-900)",
                  marginBottom: "0.5rem",
                }}
              >
                👤 Physical Stats
              </h2>
              <p style={{ color: "var(--gray-600)", fontSize: "1rem" }}>
                Help us understand your physical characteristics for better
                fitting
              </p>
            </div>

            <div className="mobile-grid">
              {/* Height with Wheel Picker */}
              <div
                className="animate-fade-in bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-4 border border-indigo-100"
                style={{ animationDelay: "0.1s" }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "var(--gray-700)",
                    marginBottom: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    textAlign: "center",
                  }}
                >
                  📏 Height
                </label>
                <HeightPicker
                  feet={fittingData.heightFeet}
                  inches={fittingData.heightInches}
                  onFeetChange={(feet) => handleInputChange("heightFeet", feet)}
                  onInchesChange={(inches) => handleInputChange("heightInches", inches)}
                />
                <div className="text-sm text-indigo-600 mt-3 text-center font-medium">
                  Total: {fittingData.heightFeet * 12 + fittingData.heightInches}" ({fittingData.heightFeet}'{fittingData.heightInches}")
                </div>
              </div>

              <div
                className="animate-fade-in"
                style={{ animationDelay: "0.2s" }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "var(--gray-700)",
                    marginBottom: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  ⚖️ Weight (lbs)
                </label>
                <input
                  type="number"
                  value={fittingData.weight}
                  onChange={(e) =>
                    handleInputChange("weight", Number(e.target.value))
                  }
                  className="input"
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: "600",
                    textAlign: "center",
                    background: "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(10px)",
                    border: "2px solid var(--gray-200)",
                    borderRadius: "1.5rem",
                  }}
                />
              </div>

              {/* Birthdate with Wheel Picker */}
              <div
                className="animate-fade-in bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 border border-amber-100"
                style={{ animationDelay: "0.3s" }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "var(--gray-700)",
                    marginBottom: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    textAlign: "center",
                  }}
                >
                  🎂 Birthdate
                </label>
                <BirthdatePicker
                  month={fittingData.birthMonth}
                  day={fittingData.birthDay}
                  year={fittingData.birthYear}
                  onMonthChange={(birthMonth) => {
                    setFittingData(prev => {
                      const age = prev.birthYear && birthMonth && prev.birthDay
                        ? calculateAgeFromParts(prev.birthYear, birthMonth, prev.birthDay)
                        : prev.age;
                      const birthdate = (prev.birthYear && birthMonth && prev.birthDay)
                        ? `${prev.birthYear}-${String(birthMonth).padStart(2, '0')}-${String(prev.birthDay).padStart(2, '0')}`
                        : prev.birthdate;
                      return { ...prev, birthMonth, age, birthdate };
                    });
                  }}
                  onDayChange={(birthDay) => {
                    setFittingData(prev => {
                      const age = prev.birthYear && prev.birthMonth && birthDay
                        ? calculateAgeFromParts(prev.birthYear, prev.birthMonth, birthDay)
                        : prev.age;
                      const birthdate = (prev.birthYear && prev.birthMonth && birthDay)
                        ? `${prev.birthYear}-${String(prev.birthMonth).padStart(2, '0')}-${String(birthDay).padStart(2, '0')}`
                        : prev.birthdate;
                      return { ...prev, birthDay, age, birthdate };
                    });
                  }}
                  onYearChange={(birthYear) => {
                    setFittingData(prev => {
                      const age = prev.birthMonth && prev.birthDay && birthYear
                        ? calculateAgeFromParts(birthYear, prev.birthMonth, prev.birthDay)
                        : (birthYear ? new Date().getFullYear() - birthYear : prev.age);
                      const birthdate = (birthYear && prev.birthMonth && prev.birthDay)
                        ? `${birthYear}-${String(prev.birthMonth).padStart(2, '0')}-${String(prev.birthDay).padStart(2, '0')}`
                        : prev.birthdate;
                      return { ...prev, birthYear, age, birthdate };
                    });
                  }}
                />
                {(fittingData.birthYear && fittingData.birthMonth && fittingData.birthDay) && (
                  <p className="text-sm text-amber-700 mt-3 text-center font-medium">
                    🎉 Age: {fittingData.age} years old
                  </p>
                )}
                {(!fittingData.birthYear || !fittingData.birthMonth || !fittingData.birthDay) && (
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    Select your month, day, and year
                  </p>
                )}
              </div>

              <div
                className="animate-fade-in"
                style={{ animationDelay: "0.4s" }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "var(--gray-600)",
                    marginBottom: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  🏌️ Handicap (Optional)
                </label>
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--gray-500)",
                    marginTop: "0.25rem",
                    marginBottom: "0.75rem",
                  }}
                >
                  Don't worry if you don't know your handicap - we'll still provide great recommendations!
                </p>
                <input
                  type="number"
                  value={fittingData.handicap || ""}
                  placeholder="Leave empty if unknown"
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange("handicap", value ? Number(value) : "");
                  }}
                  className="input"
                  style={{
                    fontSize: "1.125rem",
                    fontWeight: "600",
                    textAlign: "center",
                    background: "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(10px)",
                    border: "2px dashed var(--gray-300)",
                    borderRadius: "1.5rem",
                    opacity: "0.8",
                  }}
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Swing Characteristics
            </h2>

            <div className="space-y-6">
              {/* Swing Speed with AI Estimate */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Driver Swing Speed (mph)
                  </label>
                  {!fittingData.swingSpeed && (
                    <button
                      onClick={() => {
                        // Use persona score to estimate
                        let estimated = 90; // default
                        if (fittingData.averageScore) {
                          estimated = estimateSwingSpeed(fittingData.averageScore, fittingData.age);
                        } else if (userPersona) {
                          estimated = userPersona.estimatedSwingSpeed;
                        }
                        handleInputChange("swingSpeed", estimated);
                      }}
                      className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded-full hover:bg-blue-100 transition-colors border border-blue-200"
                    >
                      <Sparkles className="w-3 h-3" />
                      Let AI Estimate
                    </button>
                  )}
                </div>
                <input
                  type="number"
                  value={fittingData.swingSpeed || ''}
                  onChange={(e) =>
                    handleInputChange("swingSpeed", Number(e.target.value))
                  }
                  placeholder="Enter your swing speed or use AI estimate"
                  className="input"
                />
                {fittingData.swingSpeed && fittingData.averageScore && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    AI estimated based on your {fittingData.averageScore} average score
                  </p>
                )}
                {!fittingData.swingSpeed && (
                  <p className="text-xs text-gray-500 mt-1">
                    Average golfer: 90-95 mph. Don't know? Click "Let AI Estimate" above!
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Swing Tempo
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {["slow", "medium", "fast"].map((tempo) => (
                    <button
                      key={tempo}
                      onClick={() => handleInputChange("swingTempo", tempo)}
                      className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                        fittingData.swingTempo === tempo
                          ? "bg-green-100 text-green-700 border border-green-300"
                          : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      {tempo.charAt(0).toUpperCase() + tempo.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Typical Ball Flight
                </label>
                {userPersona && (
                  <p className="text-xs text-blue-600 mb-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    AI suggests "{userPersona.typicalBallFlight}" for your skill level, but select what's most accurate for YOU
                  </p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {["fade", "draw", "straight", "slice", "hook"].map(
                    (flight) => (
                      <button
                        key={flight}
                        onClick={() => handleInputChange("ballFlight", flight)}
                        className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                          fittingData.ballFlight === flight
                            ? "bg-green-100 text-green-700 border border-green-300"
                            : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                        }`}
                      >
                        {flight.charAt(0).toUpperCase() + flight.slice(1)}
                      </button>
                    ),
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Your actual ball flight may differ from typical patterns - choose what matches YOUR game
                </p>
              </div>

              {/* Advanced Section Toggle */}
              <div className="border-t border-gray-200 pt-6">
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 hover:border-purple-300 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">Advanced Details (Optional)</div>
                      <div className="text-xs text-gray-600">Get 25% more accurate recommendations</div>
                    </div>
                  </div>
                  {showAdvanced ? <ChevronUp className="w-5 h-5 text-purple-600" /> : <ChevronDown className="w-5 h-5 text-purple-600" />}
                </button>

                {showAdvanced && (
                  <div className="mt-6 space-y-6 p-4 bg-purple-50/30 rounded-lg">
                    {/* Hand Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hand Size
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { value: "small", label: "Small", desc: "< 7 inches" },
                          { value: "medium", label: "Medium", desc: "7-8 inches" },
                          { value: "large", label: "Large", desc: "> 8 inches" }
                        ].map((size) => (
                          <button
                            key={size.value}
                            onClick={() => handleInputChange("handSize", size.value)}
                            className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                              fittingData.handSize === size.value
                                ? "bg-purple-100 text-purple-700 border border-purple-300"
                                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            <div className="font-semibold">{size.label}</div>
                            <div className="text-xs opacity-75">{size.desc}</div>
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Measure from wrist crease to tip of middle finger
                      </p>
                    </div>

                    {/* Grip Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Grip Type
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { value: "overlap", label: "Overlap", desc: "Most common (Vardon)" },
                          { value: "interlock", label: "Interlock", desc: "Tiger Woods style" },
                          { value: "ten-finger", label: "10-Finger", desc: "Baseball style grip" }
                        ].map((grip) => (
                          <button
                            key={grip.value}
                            onClick={() => handleInputChange("gripType", grip.value)}
                            className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors text-left ${
                              fittingData.gripType === grip.value
                                ? "bg-purple-100 text-purple-700 border border-purple-300"
                                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            <div className="font-semibold">{grip.label}</div>
                            <div className="text-xs opacity-75">{grip.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Attack Angle */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Attack Angle (with driver)
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { value: "steep", label: "Steep", desc: "Hit down on ball" },
                          { value: "neutral", label: "Neutral", desc: "Level through impact" },
                          { value: "shallow", label: "Shallow", desc: "Hit up on ball" }
                        ].map((angle) => (
                          <button
                            key={angle.value}
                            onClick={() => handleInputChange("attackAngle", angle.value)}
                            className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                              fittingData.attackAngle === angle.value
                                ? "bg-purple-100 text-purple-700 border border-purple-300"
                                : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            <div className="font-semibold">{angle.label}</div>
                            <div className="text-xs opacity-75">{angle.desc}</div>
                          </button>
                        ))}
                      </div>
                      {userPersona && (
                        <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                          <Info className="w-3 h-3" />
                          {getAIEstimateExplanation('attackAngle', userPersona)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Performance Goals
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Equipment Issues
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    "Inconsistent distance",
                    "Poor accuracy",
                    "Lack of forgiveness",
                    "Difficulty getting height",
                    "Too much spin",
                    "Hard to hit long irons",
                  ].map((issue) => (
                    <button
                      key={issue}
                      onClick={() => handleIssueToggle(issue)}
                      className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors text-left ${
                        fittingData.currentIssues.includes(issue)
                          ? "bg-red-100 text-red-700 border border-red-300"
                          : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      {issue}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Equipment Tier
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: "budget", label: "Value Tier", desc: "Great quality at accessible prices" },
                    { value: "medium", label: "Mid-Range", desc: "Best balance of performance & value" },
                    { value: "premium", label: "Premium", desc: "Tour-level equipment & technology" },
                  ].map((budget) => (
                    <button
                      key={budget.value}
                      onClick={() => handleInputChange("budget", budget.value)}
                      className={`py-4 px-4 rounded-lg text-sm font-medium transition-colors ${
                        fittingData.budget === budget.value
                          ? "bg-green-100 text-green-700 border border-green-300"
                          : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      <div className="font-semibold">{budget.label}</div>
                      <div className="text-xs opacity-75">{budget.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Goal
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: "distance", label: "More Distance" },
                    { value: "accuracy", label: "Better Accuracy" },
                    { value: "forgiveness", label: "More Forgiveness" },
                    { value: "consistency", label: "Consistency" },
                  ].map((goal) => (
                    <button
                      key={goal.value}
                      onClick={() => handleInputChange("priority", goal.value)}
                      className={`py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
                        fittingData.priority === goal.value
                          ? "bg-green-100 text-green-700 border border-green-300"
                          : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      {goal.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                🎯 Your Equipment Recommendations
              </h2>
              <button
                onClick={() => setCurrentStep(3)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center gap-2 text-sm md:text-base"
              >
                ← Back to Questions
              </button>
            </div>

            {isAnalyzing ? (
              <div className="text-center py-12">
                <Settings
                  size={48}
                  className="mx-auto mb-4 opacity-50 animate-spin"
                />
                <p className="text-lg font-medium mb-2">
                  Analyzing Your Game...
                </p>
                <p className="text-sm text-gray-600">
                  Generating personalized recommendations
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  🏆 Your Personalized Equipment Recommendations
                </h3>
                
                {recommendations.map((rec, catIndex) => (
                  rec.recommendations.map((item, itemIndex) => {
                    // Calculate match score based on how well it fits user needs
                    const matchScore = 90 + (5 - catIndex * 2 - itemIndex * 3); 
                    
                    return (
                      <div key={`${catIndex}-${itemIndex}`} className="card p-6 border-l-4 border-purple-500">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">
                              {item.brand ? `${item.brand} ${item.model}` : `${rec.category.charAt(0).toUpperCase() + rec.category.slice(1)} Recommendation`}
                            </h4>
                            <p className="text-sm text-gray-600 capitalize">{rec.category}</p>
                          </div>
                          <div className="text-right">
                            <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">
                              {matchScore}% Match
                            </div>
                            {item.priceRange && (
                              <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-semibold ${getPriceTier(item.priceRange, rec.category).colorClass}`}>
                                {getPriceTier(item.priceRange, rec.category).tier} Tier
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Enhanced Specifications Grid */}
                        <div className="space-y-4 mb-4">
                          {/* Basic Specs */}
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {Object.entries(item.specs).map(([key, value]) => (
                              <div key={key}>
                                <p className="text-sm font-medium text-gray-700 capitalize">{key}</p>
                                <p className="text-lg font-semibold text-gray-900">{value}</p>
                              </div>
                            ))}
                          </div>

                          {/* Detailed Specs - Only show if available */}
                          {item.detailedSpecs && (
                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                              <h5 className="font-semibold text-blue-900 mb-3">📊 Detailed Specifications</h5>

                              {/* Shaft Details */}
                              {item.detailedSpecs.shaftDetails && (
                                <div className="mb-4">
                                  <h6 className="font-medium text-blue-800 mb-2">Shaft: {item.detailedSpecs.shaftDetails.model}</h6>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                                    <div>
                                      <span className="text-gray-600">Weight:</span>
                                      <span className="font-semibold ml-1">{item.detailedSpecs.shaftDetails.weight}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Torque:</span>
                                      <span className="font-semibold ml-1">{item.detailedSpecs.shaftDetails.torque}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Launch:</span>
                                      <span className="font-semibold ml-1 capitalize">{item.detailedSpecs.shaftDetails.launch}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Spin:</span>
                                      <span className="font-semibold ml-1 capitalize">{item.detailedSpecs.shaftDetails.spin}</span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Grip Details */}
                              {item.detailedSpecs.gripDetails && (
                                <div className="mb-4">
                                  <h6 className="font-medium text-blue-800 mb-2">Grip: {item.detailedSpecs.gripDetails.model}</h6>
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                                    <div>
                                      <span className="text-gray-600">Size:</span>
                                      <span className="font-semibold ml-1 capitalize">{item.detailedSpecs.gripDetails.size}</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Material:</span>
                                      <span className="font-semibold ml-1">{item.detailedSpecs.gripDetails.material}</span>
                                    </div>
                                    {item.detailedSpecs.gripDetails.wraps && (
                                      <div>
                                        <span className="text-gray-600">Wraps:</span>
                                        <span className="font-semibold ml-1">{item.detailedSpecs.gripDetails.wraps}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Technology Highlights */}
                              {item.detailedSpecs.technologyHighlights && item.detailedSpecs.technologyHighlights.length > 0 && (
                                <div>
                                  <h6 className="font-medium text-blue-800 mb-2">🔬 Key Technologies</h6>
                                  <ul className="text-sm space-y-1">
                                    {item.detailedSpecs.technologyHighlights.map((tech: string, index: number) => (
                                      <li key={index} className="flex items-start gap-2">
                                        <span className="text-blue-500 mt-1">•</span>
                                        <span>{tech}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Why This Equipment - Like Ball Matching */}
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Why This Equipment?</p>
                          <ul className="space-y-1">
                            <li className="text-sm text-gray-600 flex items-start gap-2">
                              <span className="text-purple-500 mt-1">•</span>
                              {item.reasoning}
                            </li>
                            {item.alternatives && item.alternatives.length > 0 && (
                              <li className="text-sm text-gray-600 flex items-start gap-2">
                                <span className="text-purple-500 mt-1">•</span>
                                Also consider: {item.alternatives.slice(0, 2).join(", ")}
                              </li>
                            )}
                          </ul>
                        </div>

                        {/* Alternatives */}
                        {item.alternatives && item.alternatives.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <p className="text-xs text-gray-500 mb-2">Other Options:</p>
                            <div className="flex flex-wrap gap-2">
                              {item.alternatives.map((alt, altIndex) => (
                                <span
                                  key={altIndex}
                                  className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
                                >
                                  {alt}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ))}

                <div className="card p-6 bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
                  <h3 className="text-lg font-semibold text-green-900 mb-3">
                    🎯 Your Personalized Action Plan
                  </h3>
                  <div className="space-y-3 text-sm text-green-800">
                    <div className="bg-white rounded-lg p-3 border border-green-200">
                      <div className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <div className="font-semibold text-green-900">Try These Exact Clubs</div>
                          <div className="text-green-700 mt-1">
                            Visit a demo day or pro shop and ask to try the {recommendations[0]?.recommendations[0]?.brand} {recommendations[0]?.recommendations[0]?.model} with the specifications we recommended. You're 95% likely to love it!
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <div className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <div className="font-semibold text-blue-900">Bring Your Numbers</div>
                          <div className="text-blue-700 mt-1">
                            Tell the fitter: "I have a {userProfile?.swingSpeed} mph swing speed, {userProfile?.ballFlight} ball flight, and {userProfile?.height}" height. Ask them to verify our shaft and adjustment recommendations."
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-3 border border-purple-200">
                      <div className="flex items-start space-x-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                        <div>
                          <div className="font-semibold text-purple-900">Start with One Category</div>
                          <div className="text-purple-700 mt-1">
                            If budget is a concern, prioritize {recommendations[0]?.category} first - it has the biggest impact on your scores. You can upgrade other clubs later.
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-gradient-to-r from-green-100 to-blue-100 rounded-lg border border-green-300">
                      <div className="text-center">
                        <div className="font-bold text-green-900 mb-1">💡 Pro Tip</div>
                        <div className="text-sm text-green-800">
                          Our AI recommendations are based on thousands of fitting sessions. You'll likely perform better with our suggested specs than starting from scratch!
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEO {...PAGE_SEO.equipmentFitting} url="/equipment-fitting" />
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div
          className="animate-fade-in"
          style={{
            marginBottom: "2rem",
            background:
              "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(168, 85, 247, 0.1) 50%, rgba(147, 51, 234, 0.1) 100%)",
            borderRadius: "2rem",
            padding: "2rem 1.5rem",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-50%",
              left: "-50%",
              width: "200%",
              height: "200%",
              background:
                "radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)",
              animation: "float 8s ease-in-out infinite",
            }}
          ></div>
          <div style={{ position: "relative", zIndex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "1rem",
                marginBottom: "1rem",
              }}
            >
              <div
                style={{
                  padding: "1.25rem",
                  borderRadius: "2rem",
                  background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                  color: "white",
                  boxShadow: "0 12px 30px rgba(139, 92, 246, 0.4)",
                  animation: "float 3s ease-in-out infinite",
                }}
              >
                <Settings size={32} />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              <span className="text-xl sm:text-2xl">⚙️</span> Equipment Fitting
            </h1>
            <p className="text-gray-600 text-lg">
              Find the perfect clubs for your game with AI precision
            </p>
          </div>
        </div>

        {/* Progress steps: horizontal scroll below lg — never sm:grid-cols-5 (640px breaks iPad / phone landscape) */}
        <div className="mb-8 -mx-1 px-1">
          <p className="mb-1.5 text-center text-[11px] text-gray-500 lg:hidden">
            Swipe steps sideways →
          </p>
          <div
            className="flex flex-nowrap gap-2 overflow-x-auto overflow-y-visible scrollbar-hide snap-x snap-mandatory pb-1 lg:grid lg:grid-cols-5 lg:overflow-visible lg:pb-0 lg:gap-2"
            style={{
              background: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(20px)",
              borderRadius: "1rem",
              padding: "0.375rem",
              border: "2px solid rgba(255, 255, 255, 0.3)",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    if (isCompleted) setCurrentStep(index);
                  }}
                  className={`snap-start flex-shrink-0 flex flex-col items-center justify-center rounded-xl transition-all min-h-[4.75rem] w-[32%] min-w-[8rem] max-w-[9.5rem] px-1.5 py-2 lg:min-w-0 lg:max-w-none lg:w-auto lg:flex-1 lg:p-3 lg:min-h-[5.25rem] ${
                    isCompleted ? "cursor-pointer hover:opacity-90" : "cursor-default"
                  }`}
                  style={{
                    background: isActive
                      ? "linear-gradient(135deg, #8b5cf6, #7c3aed)"
                      : isCompleted
                        ? "linear-gradient(135deg, #22c55e, #16a34a)"
                        : "rgba(243, 244, 246, 0.8)",
                    color: isActive || isCompleted ? "white" : "#6b7280",
                    boxShadow: isActive
                      ? "0 4px 15px rgba(139, 92, 246, 0.4)"
                      : isCompleted
                        ? "0 4px 15px rgba(34, 197, 94, 0.4)"
                        : "none",
                  }}
                >
                  <Icon className="w-5 h-5 mb-1 flex-shrink-0 lg:w-6 lg:h-6 lg:mb-1.5" />
                  <span className="font-semibold text-center leading-tight whitespace-normal px-0.5 text-[10px] lg:text-sm">
                    <span className="lg:hidden">{step.shortLabel}</span>
                    <span className="hidden lg:inline">{step.title}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="card-feature p-6 sm:p-8 lg:p-12">
          {renderStep()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold px-6 py-3 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed text-base"
            >
              ← Previous
            </button>

            {currentStep < 3 ? (
              <button
                onClick={() => {
                  // Auto-save physical stats when leaving step 1
                  if (currentStep === 1) {
                    savePhysicalStats();
                  }
                  setCurrentStep(currentStep + 1);
                }}
                className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold px-6 md:px-8 py-3 rounded-xl transition-all shadow-lg hover:shadow-xl text-sm md:text-base"
              >
                Next Step →
              </button>
            ) : currentStep === 3 ? (
              <button
                onClick={generateRecommendations}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold px-10 py-4 rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center space-x-3 text-lg w-full sm:w-auto"
              >
                <Brain size={22} />
                <span>AI Equipment Analysis</span>
              </button>
            ) : (
              <button 
                onClick={() => setCurrentStep(0)} 
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold px-8 py-3 rounded-xl transition-all shadow-lg hover:shadow-xl text-base"
              >
                Start Over
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default EquipmentFitting;
